import React, { useEffect, useMemo, useState } from "react";
import { stageAssignmentsAPI } from "../../api/client";
import { formatAssignmentStatus } from "../../utils/formatAssignmentStatus";

const formatNoteDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const fullName = (person) =>
  `${person?.firstName || ""} ${person?.lastName || ""}`.trim() || "Someone";

const initials = (person) =>
  `${person?.firstName?.[0] || ""}${person?.lastName?.[0] || ""}`.toUpperCase();

/**
 * Per-assignee work instructions for one stage.
 *
 * Every active assignee gets a card, all visible at once and saved together,
 * because dividing work is a comparative act — you cannot write one person's
 * instruction well without seeing the others'. A blank box is skipped rather
 * than rejected, so reopening the panel to correct one person does not force
 * the rest to be rewritten.
 */
export default function StageInstructionsPanel({
  projectId,
  stageId,
  assignments = [],
  disabled = false,
}) {
  const [notes, setNotes] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [expandedHistory, setExpandedHistory] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [savedAt, setSavedAt] = useState(null);
  const [justSent, setJustSent] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (!projectId || !stageId) return undefined;
    stageAssignmentsAPI
      .getStageNotes(projectId, stageId)
      .then((resp) => {
        if (!cancelled) setNotes(resp.data || []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(
            err.response?.data?.message || "Failed to load stage instructions",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, [projectId, stageId, savedAt]);

  // The "Sent" confirmation is transient — it acknowledges the click and then
  // gets out of the way, so the panel does not carry stale state if the manager
  // leaves the tab open.
  useEffect(() => {
    if (!justSent) return undefined;
    const timer = setTimeout(() => setJustSent(0), 4000);
    return () => clearTimeout(timer);
  }, [justSent]);

  // Instructions already written to each assignee, oldest first. Only notes
  // aimed AT the assignee belong here — their own replies live in the thread
  // on their work page, not in the manager's compose view.
  const historyByAssignment = useMemo(() => {
    const map = new Map();
    notes
      .filter((note) => note.direction === "TO_ASSIGNEE")
      .forEach((note) => {
        const key = note.stageAssignmentId;
        if (!key) return;
        if (!map.has(key)) map.set(key, []);
        map.get(key).push(note);
      });
    return map;
  }, [notes]);

  const setDraft = (assignmentId, value) =>
    setDrafts((prev) => ({ ...prev, [assignmentId]: value }));

  const pending = Object.entries(drafts)
    .map(([assignmentId, body]) => ({
      assignmentId,
      body: (body || "").trim(),
    }))
    .filter((entry) => entry.body);

  const save = async () => {
    if (!pending.length) return;
    setSaving(true);
    setError("");
    try {
      await stageAssignmentsAPI.addStageInstructions(
        projectId,
        stageId,
        pending,
      );
      setDrafts({});
      setJustSent(pending.length);
      setSavedAt(Date.now());
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          "Failed to save instructions",
      );
    } finally {
      setSaving(false);
    }
  };

  // Ctrl/Cmd+Enter sends everything pending, not just the box in focus: the
  // save is a single batch, and sending only one person's share would leave the
  // others silently unsent.
  const handleKeyDown = (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
      e.preventDefault();
      if (!disabled && !saving && pending.length) save();
    }
  };

  if (!assignments.length) return null;

  const totalSent = assignments.reduce(
    (sum, assignment) =>
      sum + (historyByAssignment.get(assignment.id)?.length || 0),
    0,
  );

  return (
    <div className="instructions-panel">
      <div className="instructions-header">
        <div className="instructions-header-text">
          <h4 className="instructions-title">Work Instructions</h4>
          <span className="instructions-subtitle">
            Tell each assignee what they are responsible for. They will see it
            at the top of their task page.
          </span>
        </div>
        <span className="instructions-count">
          {assignments.length} assignee{assignments.length === 1 ? "" : "s"}
          {totalSent > 0 && ` · ${totalSent} sent`}
        </span>
      </div>

      <div className="instruction-cards">
        {assignments.map((assignment) => {
          const history = historyByAssignment.get(assignment.id) || [];
          const latest = history[history.length - 1] || null;
          const earlier = history.slice(0, -1);
          const isOpen = !!expandedHistory[assignment.id];
          const status = formatAssignmentStatus(assignment.status);
          const draft = drafts[assignment.id] || "";

          return (
            <div key={assignment.id} className="instruction-card">
              <div className="instruction-card-head">
                <div className="instruction-who">
                  <div className="assignee-avatar">
                    {initials(assignment.assignee)}
                  </div>
                  <div className="instruction-who-text">
                    <span className="assignee-name">
                      {fullName(assignment.assignee)}
                    </span>
                    <span className="instruction-sent-count">
                      {history.length
                        ? `${history.length} instruction${
                            history.length === 1 ? "" : "s"
                          } sent`
                        : "No instructions yet"}
                    </span>
                  </div>
                </div>
                <span
                  className="instruction-status-badge"
                  style={{
                    background: status.bgColor,
                    color: status.textColor,
                  }}
                >
                  {status.label}
                </span>
              </div>

              {latest ? (
                <div className="instruction-current">
                  <div className="instruction-current-body">{latest.body}</div>
                  <div className="instruction-current-meta">
                    <span className="instruction-meta-author">
                      {fullName(latest.author)} ·{" "}
                      {formatNoteDate(latest.createdAt)}
                    </span>
                    {earlier.length > 0 && (
                      <button
                        type="button"
                        className="instruction-history-toggle"
                        onClick={() =>
                          setExpandedHistory((prev) => ({
                            ...prev,
                            [assignment.id]: !isOpen,
                          }))
                        }
                      >
                        {isOpen
                          ? "Hide earlier"
                          : `Show ${earlier.length} earlier`}
                      </button>
                    )}
                  </div>

                  {isOpen && (
                    <div className="instruction-earlier-list">
                      {earlier
                        .slice()
                        .reverse()
                        .map((note) => (
                          <div key={note.id} className="instruction-earlier">
                            <div className="instruction-earlier-body">
                              {note.body}
                            </div>
                            <div className="instruction-meta-author">
                              {fullName(note.author)} ·{" "}
                              {formatNoteDate(note.createdAt)}
                            </div>
                          </div>
                        ))}
                    </div>
                  )}
                </div>
              ) : (
                <p className="instruction-empty">
                  {disabled
                    ? "No instructions were sent for this stage."
                    : `${assignment.assignee?.firstName || "This person"} has not been told what to focus on yet.`}
                </p>
              )}

              {!disabled && (
                <div className="instruction-compose">
                  <textarea
                    className="instruction-input"
                    rows={2}
                    value={draft}
                    onChange={(e) => setDraft(assignment.id, e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={
                      latest
                        ? `Add a new instruction for ${assignment.assignee?.firstName || "them"}...`
                        : `e.g. ${assignment.assignee?.firstName || "You"} — take Task C and Task D`
                    }
                    disabled={saving}
                  />
                  {draft.trim() && (
                    <span className="instruction-draft-flag">
                      Queued to send
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {!disabled && (
        <div className="instructions-actions">
          <button
            className="btn-primary btn-sm"
            onClick={save}
            disabled={saving || !pending.length}
            title={!pending.length ? "Write at least one instruction" : ""}
          >
            {saving
              ? "Sending..."
              : pending.length > 1
                ? `Send ${pending.length} instructions`
                : "Send instruction"}
          </button>
          <span className="instructions-hint">
            Ctrl + Enter to send
          </span>
          {justSent > 0 && !pending.length && (
            <span className="instructions-sent">
              ✓ Sent to {justSent} assignee{justSent === 1 ? "" : "s"}
            </span>
          )}
          {error && <span className="instructions-error">{error}</span>}
        </div>
      )}

      {disabled && error && (
        <span className="instructions-error">{error}</span>
      )}
    </div>
  );
}
